import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Layout/Header';
import { dashboardAPI, leaveAPI } from '../lib/api';
import { formatCurrency, formatDate, formatMonth, getStatusBadgeClass, capitalizeFirst } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Calendar, Wallet, Clock, Plus, User, Building2, FileText, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';

const EmployeeDashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isLeaveModalOpen, setIsLeaveModalOpen] = useState(false);
  const [leaveForm, setLeaveForm] = useState({
    leave_type: 'casual',
    start_date: '',
    end_date: '',
    reason: '',
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const response = await dashboardAPI.getEmployee();
      setData(response.data);
    } catch (error) {
      toast.error('Failed to fetch dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const handleApplyLeave = async (e) => {
    e.preventDefault();
    try {
      await leaveAPI.apply(leaveForm);
      toast.success('Leave application submitted');
      setIsLeaveModalOpen(false);
      setLeaveForm({ leave_type: 'casual', start_date: '', end_date: '', reason: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to apply for leave');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner w-8 h-8"></div>
      </div>
    );
  }

  const leaveBalance = data?.leave_balance || {};

  return (
    <div data-testid="employee-dashboard">
      <Header 
        title={`Welcome, ${data?.employee?.name || 'Employee'}`} 
        subtitle="Your personal HR dashboard" 
      />

      <div className="p-8">
        {/* Employee Info Card */}
        <div className="dashboard-card mb-8 relative overflow-hidden">
          <div 
            className="absolute top-0 right-0 w-64 h-64 opacity-10"
            style={{
              backgroundImage: 'url(https://images.pexels.com/photos/3434997/pexels-photo-3434997.jpeg)',
              backgroundSize: 'cover',
              backgroundPosition: 'center',
            }}
          ></div>
          <div className="relative flex items-start gap-6">
            <div className="w-20 h-20 rounded-xl bg-slate-900 flex items-center justify-center">
              <span className="text-2xl font-heading font-semibold text-white">
                {data?.employee?.name?.charAt(0)}
              </span>
            </div>
            <div className="flex-1">
              <h2 className="font-heading text-2xl font-semibold text-slate-900 mb-1">
                {data?.employee?.name}
              </h2>
              <p className="text-slate-600 mb-4">{data?.employee?.designation}</p>
              <div className="flex flex-wrap gap-6 text-sm">
                <div className="flex items-center gap-2 text-slate-600">
                  <Building2 className="w-4 h-4" />
                  <span>{data?.department?.name}</span>
                </div>
                <div className="flex items-center gap-2 text-slate-600">
                  <User className="w-4 h-4" />
                  <span>{data?.employee?.employee_code}</span>
                </div>
                <div className="flex items-center gap-2 text-slate-600">
                  <Calendar className="w-4 h-4" />
                  <span>Joined {formatDate(data?.employee?.date_of_joining)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Leave Balance & Actions */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-8">
          {/* Leave Balance */}
          <div className="lg:col-span-2 dashboard-card">
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-heading text-lg font-semibold text-slate-900">Leave Balance</h3>
              <Button onClick={() => setIsLeaveModalOpen(true)} data-testid="apply-leave-btn">
                <Plus className="w-4 h-4 mr-2" />
                Apply Leave
              </Button>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="p-4 bg-blue-50 rounded-lg">
                <p className="text-xs font-medium text-blue-600 uppercase">Casual</p>
                <p className="text-2xl font-semibold text-blue-900 mt-1">
                  {(leaveBalance.casual_leave || 12) - (leaveBalance.casual_leave_used || 0)}
                </p>
                <p className="text-xs text-blue-600">of {leaveBalance.casual_leave || 12}</p>
              </div>
              <div className="p-4 bg-emerald-50 rounded-lg">
                <p className="text-xs font-medium text-emerald-600 uppercase">Sick</p>
                <p className="text-2xl font-semibold text-emerald-900 mt-1">
                  {(leaveBalance.sick_leave || 12) - (leaveBalance.sick_leave_used || 0)}
                </p>
                <p className="text-xs text-emerald-600">of {leaveBalance.sick_leave || 12}</p>
              </div>
              <div className="p-4 bg-purple-50 rounded-lg">
                <p className="text-xs font-medium text-purple-600 uppercase">Earned</p>
                <p className="text-2xl font-semibold text-purple-900 mt-1">
                  {(leaveBalance.earned_leave || 15) - (leaveBalance.earned_leave_used || 0)}
                </p>
                <p className="text-xs text-purple-600">of {leaveBalance.earned_leave || 15}</p>
              </div>
              <div className="p-4 bg-amber-50 rounded-lg">
                <p className="text-xs font-medium text-amber-600 uppercase">Unpaid Used</p>
                <p className="text-2xl font-semibold text-amber-900 mt-1">
                  {leaveBalance.unpaid_leave_used || 0}
                </p>
                <p className="text-xs text-amber-600">days</p>
              </div>
            </div>
          </div>

          {/* Latest Payslip */}
          <Link to="/my-payslips" className="dashboard-card hover:shadow-lg transition-shadow cursor-pointer">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading text-lg font-semibold text-slate-900">Latest Salary</h3>
              <div className="flex items-center gap-2">
                <Wallet className="w-5 h-5 text-slate-400" />
                <ChevronRight className="w-4 h-4 text-slate-400" />
              </div>
            </div>
            {data?.recent_salaries?.[0] ? (
              <div className="text-center py-4">
                <p className="text-sm text-slate-500 mb-1">{formatMonth(data.recent_salaries[0].month)}</p>
                <p className="text-3xl font-heading font-semibold text-emerald-600">
                  {formatCurrency(data.recent_salaries[0].net_salary || data.recent_salaries[0].net_pay)}
                </p>
                <p className="text-xs text-slate-400 mt-2">Net Pay</p>
              </div>
            ) : (
              <p className="text-center text-slate-500 py-4">No salary records</p>
            )}
          </Link>
        </div>

        {/* Recent Activity */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Salary History */}
          <div className="dashboard-card">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading text-lg font-semibold text-slate-900">Salary History</h3>
              <Link to="/my-payslips" className="text-sm text-blue-600 hover:underline flex items-center gap-1">
                View All <ChevronRight className="w-4 h-4" />
              </Link>
            </div>
            {data?.recent_salaries?.length > 0 ? (
              <div className="space-y-3">
                {data.recent_salaries.map((salary) => (
                  <Link
                    to="/my-payslips"
                    key={salary.id}
                    className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
                    data-testid={`salary-${salary.id}`}
                  >
                    <div>
                      <p className="font-medium text-slate-900">{formatMonth(salary.month)}</p>
                      <p className="text-xs text-slate-500">
                        Gross: {formatCurrency(salary.gross_earnings || salary.gross_salary)}
                      </p>
                    </div>
                    <p className="font-semibold text-emerald-600">{formatCurrency(salary.net_salary || salary.net_pay)}</p>
                  </Link>
                ))}
              </div>
            ) : (
              <p className="text-center text-slate-500 py-4">No salary records</p>
            )}
          </div>

          {/* Leave History */}
          <div className="dashboard-card">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading text-lg font-semibold text-slate-900">Recent Leave Applications</h3>
              <Link to="/my-leaves" className="text-sm text-blue-600 hover:underline flex items-center gap-1">
                View All <ChevronRight className="w-4 h-4" />
              </Link>
            </div>
            {data?.leave_history?.length > 0 ? (
              <div className="space-y-3">
                {data.leave_history.map((leave) => (
                  <Link
                    to="/my-leaves"
                    key={leave.id}
                    className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
                    data-testid={`leave-${leave.id}`}
                  >
                    <div>
                      <p className="font-medium text-slate-900 capitalize">{leave.leave_type} Leave</p>
                      <p className="text-xs text-slate-500">
                        {formatDate(leave.start_date)} - {formatDate(leave.end_date)} ({leave.days} days)
                      </p>
                    </div>
                    <span className={getStatusBadgeClass(leave.status)}>
                      {capitalizeFirst(leave.status)}
                    </span>
                  </Link>
                ))}
              </div>
            ) : (
              <p className="text-center text-slate-500 py-4">No leave applications</p>
            )}
          </div>
        </div>
      </div>

      {/* Apply Leave Modal */}
      <Dialog open={isLeaveModalOpen} onOpenChange={setIsLeaveModalOpen}>
        <DialogContent data-testid="leave-modal" aria-describedby="leave-form-description">
          <DialogHeader>
            <DialogTitle className="font-heading">Apply for Leave</DialogTitle>
            <p id="leave-form-description" className="text-sm text-slate-500">Fill in the details below to submit your leave request</p>
          </DialogHeader>

          <form onSubmit={handleApplyLeave} className="space-y-4">
            <div className="space-y-2">
              <Label>Leave Type</Label>
              <Select
                value={leaveForm.leave_type}
                onValueChange={(val) => setLeaveForm({ ...leaveForm, leave_type: val })}
              >
                <SelectTrigger data-testid="select-leave-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="casual">Casual Leave</SelectItem>
                  <SelectItem value="sick">Sick Leave</SelectItem>
                  <SelectItem value="earned">Earned Leave</SelectItem>
                  <SelectItem value="unpaid">Unpaid Leave</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Start Date</Label>
                <Input
                  type="date"
                  value={leaveForm.start_date}
                  onChange={(e) => setLeaveForm({ ...leaveForm, start_date: e.target.value })}
                  required
                  data-testid="input-start-date"
                />
              </div>
              <div className="space-y-2">
                <Label>End Date</Label>
                <Input
                  type="date"
                  value={leaveForm.end_date}
                  onChange={(e) => setLeaveForm({ ...leaveForm, end_date: e.target.value })}
                  required
                  data-testid="input-end-date"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Reason</Label>
              <Textarea
                value={leaveForm.reason}
                onChange={(e) => setLeaveForm({ ...leaveForm, reason: e.target.value })}
                placeholder="Enter reason for leave"
                required
                data-testid="input-reason"
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsLeaveModalOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" data-testid="submit-leave-btn">
                Submit Application
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EmployeeDashboard;
